import type { Duration } from '@moojo/duration'
import type { ParserOf } from '@moojo/misc'

import type { RequestInfo } from './request-info'

/**
 * The type of a function (typically on the client side) that can invoke an endpoint
 */
export type Dispatch<Req, Resp> = (request: Req) => Promise<Resp>

export type AddIngesterDetails = (details: Record<string, string | number>) => void

export type AddResponseHeader = (name: string, value: string) => void

/**
 * An object that can process an incoming request and generate an output off of it.
 */
export interface Handler<Req, Resp> {
  // TODO(imaman): scope can be useful but it's currently not used. consider retiring.
  /**
   * @param request the input to process
   * @param requestInfo additional pieces of information (metadata) about the request.
   * @param addIngesterDetails when invoked adds endpoint specific details to rpcCompleted event.
   */
  handle(
    request: Req,
    requestInfo: RequestInfo,
    addIngesterDetails: AddIngesterDetails,
    addResponseHeader: AddResponseHeader,
  ): Promise<Resp>

  /**
   * Allows the endpoint to return HTTP responses.
   *
   * @param request - The parsed request object
   * @param requestInfo - Information about the HTTP request
   * @param addIngesterDetails - Function to add details for metrics ingestion
   * @returns An object containing the response body as a string and custom headers
   */
  handleHttp?: (
    request: Req,
    requestInfo: RequestInfo,
    addIngesterDetails: AddIngesterDetails,
  ) => Promise<{ body: string; statusCode?: number; headers: Partial<Record<string, string>> }>

  /**
   * A method that allows the endpoint to process several requests at once, to be used when inputs events are passed
   * through a batching mechanism (such AWS SQS) before reaching the service. Currently ignored.
   *
   * @param batch
   * @param requestInfo
   * @param addIngesterDetails
   * @returns
   */
  handleBatch?: (batch: Req[], requestInfo: RequestInfo, addIngesterDetails: AddIngesterDetails) => Promise<void>

  /**
   * Execution threshold is per-endpoint optional function that allows setting an expected upper bound on execution time.
   * This is useful for endpoints with insufficient traffic to reliably compute the 99th percentile (https://en.wikipedia.org/wiki/Percentile),
   * ensuring performance constraints can still be defined in low-traffic scenarios.
   * @returns the maximum time this handler is expected to take
   */
  executionThreshold?: () => Promise<Duration>
}

export interface EndpointDeclaration<Req, Resp> {
  reqParser: ParserOf<Req>
  respParser: ParserOf<Resp>
}

export function endpointDeclaration<Req, Resp>(
  reqParser: ParserOf<Req>,
  respParser: ParserOf<Resp>,
): EndpointDeclaration<Req, Resp> {
  return {
    reqParser,
    respParser,
  }
}

export interface BoundedDeclaration<Req, Resp> {
  decl: EndpointDeclaration<Req, Resp>
  endpoint: Handler<Req, Resp>
}

type HandlerFunction<Req, Resp> = (
  req: Req,
  httpRequestInfo: RequestInfo,
  addIngesterDetails: AddIngesterDetails,
  addResponseHeader: AddResponseHeader,
) => Resp | Promise<Resp>
type HandlerOrFunction<Req, Resp> = Handler<Req, Resp> | HandlerFunction<Req, Resp>

export function bindDeclaration<Req, Resp>(
  decl: EndpointDeclaration<Req, Resp>,
  handler: HandlerOrFunction<Req, Resp>,
): BoundedDeclaration<Req, Resp> {
  if (typeof handler === 'function') {
    handler = new DelegatingHandler<Req, Resp>(handler)
  }
  return { decl, endpoint: handler }
}

class DelegatingHandler<Req, Resp> implements Handler<Req, Resp> {
  constructor(private readonly handlerFunction: HandlerFunction<Req, Resp>) {}

  handle(
    req: Req,
    httpRequestInfo: RequestInfo,
    addIngesterDetails: AddIngesterDetails,
    addResponseHeader: AddResponseHeader,
  ): Promise<Resp> {
    return Promise.resolve(this.handlerFunction(req, httpRequestInfo, addIngesterDetails, addResponseHeader))
  }
}
